import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { AgGridReact } from 'ag-grid-react';

import { CSSObjectWithLabel } from 'react-select';
import { Tooltip } from '@tag/tag-components-react-v4';

import styles from './pause-contract-line-modal.module.scss';

import DatePickerTag4 from '@/components/common/DatePicker/DatePickerTag4';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import { isValidDate, toMutationDateString } from '@/utils/date.utils';
import { initialValues, validationSchema } from '../PauseBilling/validation';
import { PAUSE_BILLING_TYPE } from '@/config/reasonTypes.config';
import { PauseContractLineParams } from '../../hooks/usePauseContractLine';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import { FormattingUtils } from '@/utils/Formatting.utils';
import ModalFormTextInput from '@/components/business/ModalForm/ModalFormTextInput/ModalFormTextInput';
import TopLabel from '@/components/common/TopLabel/TopLabel';
import ValidationAlert from '@/components/common/ValidationAlert/ValidationAlert';
import { ContractLineSelectionPayload } from 
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { groupSelectedLinesByContract } from '@/utils/contractLineHelpers';
import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import { getPauseContractLineColumns } from './pauseContractLineColumns';
import AgGridSelectableFilter 
  from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/AgGridSelectableFilter';
import AgGridDatepickerTag4Editor 
  from '@/components/common/TableGrid/CustomComponents/AgGridDatepicker/AgGridDatepickerTag4Editor';
import AgGridDropdownEditor 
  from '@/components/common/TableGrid/CustomComponents/AgGridDropdown/AgGridDropdownEditor';
import AgGridCellRenderer 
  from '@/components/common/TableGrid/CustomComponents/AgGridCellRenderer/AgGridCellRenderer';
import { ContractLineValidator } from '@/services/validation/ContractLineValidator';
import { pauseContractLineGridValidationSchema } from './gridValidationSchema';
import { EnvUtils } from '@/utils/env.utils';

interface IPauseContractLineModalProps {
  onClose: () => void;
  pauseContractLine: (
    inputParams: PauseContractLineParams, 
    includeRegularLines?: boolean, 
    includeVariableLines?: boolean
  ) => void;
  reasonCodes: ReasonCode[];
  reasonTypes: ReasonType[];
  selectedLines: Array<{ id: string; payload: ContractLineSelectionPayload }>;
  selectedVariableLines?: Array<{ id: string; payload: ContractLineSelectionPayload }>;
  isLoadingReasonCodes?: boolean;
  reasonCodesWithDisabled?: ReasonCode[];
  readOnly?: boolean;
}

const EMPTY_VARIABLE_LINES: Array<{ id: string; payload: ContractLineSelectionPayload }> = [];
const EMPTY_REASON_CODES: ReasonCode[] = [];

interface PauseBillingData {
  startDate?: string;
  endDate?: string;
  comment?: string;
  reference?: string;
  reasonCode?: string;
}

const extractPauseBillingData = (line: any): PauseBillingData | null => {
  const contractLine = line.payload?.contractLine;
  if (!contractLine) return null;
  
  return {
    startDate: contractLine.lineBillingCurrentStatusStartDate || contractLine.lineBillingStatusUpdated,
    endDate: contractLine.lineBillingCurrentStatusEndDate,
    comment: contractLine.lineBillingStatusUpdateComment,
    reference: contractLine.lineBillingStatusUpdateReference,
    reasonCode: contractLine.lineBillingStatusUpdateReasonCode || ''
  };
};

const getCommonPauseBillingData = (selectedLines: any[]): PauseBillingData | null => {
  if (selectedLines.length === 0) return null;
  
  const pauseBillingDataArray = selectedLines.map(extractPauseBillingData);
  
  if (pauseBillingDataArray.some(data => data === null)) return null;
  
  const firstData = pauseBillingDataArray[0]!;
  
  const allIdentical = pauseBillingDataArray.every(data => 
    data &&
    data.endDate === firstData.endDate &&
    data.comment === firstData.comment &&
    data.reference === firstData.reference &&
    data.reasonCode === firstData.reasonCode
  );
  
  return allIdentical ? firstData : null;
};

const getCommonStartDate = (selectedLines: any[]): string | undefined => {
  if (selectedLines.length === 0) return undefined;
  
  const startDates = selectedLines.map(line => {
    const contractLine = line.payload?.contractLine;
    return contractLine?.lineBillingCurrentStatusStartDate || contractLine?.lineBillingStatusUpdated;
  });
  
  const firstStartDate = startDates[0];
  const allSameStartDate = startDates.every(date => date === firstStartDate);
  
  return allSameStartDate ? firstStartDate : undefined;
};

export default function PauseContractLineModal({
  onClose,
  pauseContractLine,
  reasonCodes,
  reasonTypes,
  selectedLines,
  selectedVariableLines = EMPTY_VARIABLE_LINES,
  isLoadingReasonCodes,
  reasonCodesWithDisabled = EMPTY_REASON_CODES,
  readOnly,
}: IPauseContractLineModalProps) {

  const [isWarningVisible, setIsWarningVisible] = useState<boolean>(false);
  const gridRef = useRef<AgGridReact>(null);
  const [editedRows, setEditedRows] = useState<Set<string>>(new Set());
  const [startDateHasChanged, setStartDateHasChanged] = useState<boolean>(false);

  const reasonCodesRef = useRef<ReasonCode[]>([]);

  useEffect(() => {
    reasonCodesRef.current = reasonCodesWithDisabled;
  }, [reasonCodesWithDisabled]);
  
  const allSelectedLines = useMemo(() => [...selectedLines, ...selectedVariableLines], 
    [selectedLines, selectedVariableLines]);
  
  const commonData = useMemo(() => getCommonPauseBillingData(allSelectedLines), [allSelectedLines]);
  const isUpdate = useMemo(() => {
    return allSelectedLines.some(line => {
      const contractLine = line.payload?.contractLine as any;
      return contractLine?.lineBillingCurrentStatusEndDate || contractLine?.lineBillingStatusUpdateReasonCode;
    });
  }, [allSelectedLines]);

  const isDisableFutureLinePause = EnvUtils.isDisableFutureLinePauseEnabled();
  
  const computedStartDate = useMemo(() => {
    if (!isDisableFutureLinePause) {
      return commonData?.startDate;
    }
    
    if (isUpdate) {
      const commonStartDate = getCommonStartDate(allSelectedLines);
      return commonStartDate;
    }
    
    // For new pauses with isDisableFutureLinePause, don't send a start date.
    // The server will use its UTC time as the effective start date,
    // avoiding timezone issues where APAC users see the date converted to "tomorrow".
    return undefined;
  }, [isDisableFutureLinePause, isUpdate, allSelectedLines, commonData?.startDate]);

  const formikInitialValues = useMemo(() => ({
    ...initialValues,
    startDate: computedStartDate,
    endDate: commonData?.endDate,
    comment: commonData?.comment || null,
    reference: commonData?.reference || null,
    reasonCode: commonData?.reasonCode || ''
  }), [computedStartDate, commonData?.endDate, commonData?.comment, commonData?.reference, commonData?.reasonCode]);

  const { values, setFieldValue, errors, isValid, dirty, setFieldTouched, touched } = useFormik({
    initialValues: formikInitialValues,
    validationSchema,
    onSubmit: () => undefined,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const rowData = useMemo(() => {
    return allSelectedLines.map(line => ({ ...line.payload.contractLine }));
  }, [allSelectedLines]);

  const onSubmitMainForm = (e: FormEvent) => {
    e.preventDefault();
    setIsWarningVisible(true);
  };

  const onPauseContractLine = () => {
    // Combine all selected lines into one array
    const allContracts = groupSelectedLinesByContract(allSelectedLines);

    // Smart detection: determine which queries need refetching
    // - If only variable lines selected: refetch only variable query
    // - If only regular lines selected: refetch only regular query  
    // - If both selected: refetch both queries
    const hasRegularLines = selectedLines.length > 0;
    const hasVariableLines = selectedVariableLines.length > 0;

    // Single mutation call with combined data
    pauseContractLine(
      {
        contracts: allContracts,
        startDate: values.startDate ? toMutationDateString(values.startDate) : undefined,
        endDate: values.endDate ? toMutationDateString(values.endDate) : undefined,
        reasonCode: values.reasonCode ?? '',
        comment: values.comment,
        reference: values.reference,
      },
      hasRegularLines,
      hasVariableLines
    );
  };

  const pauseValidator = useMemo(
    () => new ContractLineValidator(pauseContractLineGridValidationSchema),
    []
  );
  const trackEditedRow = (data: any, startDateChanged?: boolean) => {
    try {
      if(startDateChanged) {
        setStartDateHasChanged(true);
      }

      pauseValidator.isValidContractLine(data);
      
      const contractLineId = data.id || data.contractLineID || data.iD;
      setEditedRows(prev => new Set(prev).add(contractLineId));
    } catch (error) {
      console.log('Error tracking edited row', error);
    }
  };

  const areAllEditedRowsValid = useMemo(() => {
    const gridApi = gridRef.current?.api;
    if (!gridApi || editedRows.size === 0) return false;

    let allValid = true;
    gridApi.forEachNode((node) => {
      const contractLineId = node.data?.id || node.data?.contractLineID || node.data?.iD;
      if (editedRows.has(contractLineId)) {
        const isLineValid = pauseValidator.isValidContractLine(node.data);
        if (!isLineValid) {
          allValid = false;
        }
      }
    });
    return allValid;
  }, [editedRows, rowData, pauseValidator]);

  const saveAllEditedRows = async () => {
    const gridApi = gridRef.current?.api;
    if (!gridApi) return;

    const editedRowsData: any[] = [];
    gridApi.forEachNode((node) => {
      const contractLineId = node.data?.id || node.data?.contractLineID || node.data?.iD;
      if (editedRows.has(contractLineId)) {
        editedRowsData.push(node.data);
      }
    });

    if (editedRowsData.length === 0) return;

    for (const editedRow of editedRowsData) {
      const contractId = editedRow.contractId || editedRow.contractID;
      const contractLineId = editedRow.id || editedRow.contractLineID || editedRow.iD;
      const isVariableLine = editedRow.__typename === 'VariableContractLineOutput';

      const contracts = [{
        contractId,
        contractLines: [{ contractLineID: contractLineId }]
      }];

      const startDateValue = startDateHasChanged
        ? toMutationDateString(editedRow.lineBillingCurrentStatusStartDate)
        : undefined;

      await pauseContractLine({
        contracts,
        startDate: startDateValue,
        endDate: toMutationDateString(editedRow.lineBillingCurrentStatusEndDate),
        reasonCode: editedRow.lineBillingStatusUpdateReasonCode || undefined,
        comment: editedRow.lineBillingStatusUpdateComment || undefined,
        reference: editedRow.lineBillingStatusUpdateReference || undefined
      },
      !isVariableLine,
      isVariableLine);
    }

    setEditedRows(new Set());
    setStartDateHasChanged(false);
  };

  const getValidationErrors = (colDef: any, data: any): string[] => {
    const contractLineId = data.contractLineID || data.id || data.iD;
    const hasBeenEdited = editedRows.has(contractLineId);
    return hasBeenEdited ? pauseValidator.getErrorsAt(colDef?.field!, data) : [];
  };

  const columns = getPauseContractLineColumns({ 
    reasonCodes: reasonCodes.length > 0 ? reasonCodes : reasonCodesWithDisabled,
    trackEditedRow,
    getValidationErrors,
    isDisableFutureLinePause
  });

  if (isWarningVisible) {
    return (
      <AgConfirmationModal
        isOpen={true}
        title={`You are about to ${isUpdate ? 'modify' : 'pause'} billing for selected contract lines`}
        onClose={onClose}
        onConfirm={onPauseContractLine}
        contentStyle={{ width: 680 }}
      >
        {isUpdate ? (
          <>
            You are about to modify the pause billing details for the selected contract lines.
            <br />
            <br />
            <strong>Selected contract lines: {allSelectedLines.length}</strong>
            <br />
            Please confirm to proceed further.
          </>
        ) : (
          <>
            Pausing the billing means stopping all billing activities for the selected contract lines 
            until they are resumed.
            <br />
            <br />
            <strong>Selected contract lines: {allSelectedLines.length}</strong>
            <br />
            Use it only with extra precaution.
          </>
        )}
      </AgConfirmationModal>
    );
  }

  return (
    <AgModalWindow
      title={
        `${isUpdate ? 'Update' : 'Pause'} Billing - ${allSelectedLines.length} 
        Contract Line${allSelectedLines.length > 1 ? 's' : ''}`
      }
      isOpen={true}
      onClose={onClose}
      contentStyle={{ width: '90vw' }}
    >
      {readOnly && (
        <div
          style={{
            padding: '10px 15px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#856404',
            marginBottom: '15px',
          }}
        >
          <strong>Note: </strong>Only users with Administrator or Full Access roles can edit information.
        </div>
      )}

      <form onSubmit={onSubmitMainForm}>
        <div className={styles['pause-contract-line-container']}>
          <div className={styles['pause-contract-line-main']}>
            <div className={styles['pause-contract-line-main-row']}>
              <ModalFormDropdown<ReasonType>
                id="reasonType"
                labelTitle="Reason Type"
                className={styles['row-field']}
                optionsData={reasonTypes}
                getOptionLabel={(option) => option.formattedType}
                getOptionValue={(option) => option.type}
                disabled
                defaultValue={reasonTypes.find((el) => el.type === PAUSE_BILLING_TYPE)}
              />

              <ModalFormDropdown<ReasonCode>
                id="reasonCode"
                labelTitle="Reason Name"
                isSearchable={true}
                className={styles['row-field']}
                optionsData={reasonCodes}
                placeholder="Select or type to search"
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.code}
                getFormatOptionLabel={(option, optionLabelMeta) => (
                  optionLabelMeta.context === 'menu' && option.description ? (
                    <Tooltip 
                      tooltipFor={<span>{option.name}</span>}
                      style={{ zIndex: 10000 }}
                    >
                      {option.description}
                    </Tooltip>
                  ) : (
                    option.name
                  )
                )}
                selectedOption={reasonCodes.find((el) => el.code === values.reasonCode) ?? null}
                menuPortalTarget={document.querySelector('body')}
                customStyles={{
                  menuList: (provided) => ({ ...provided, maxHeight: 200 } as CSSObjectWithLabel)
                }}
                handleChange={(newValue) => {
                  setFieldTouched('reasonCode', true, false);
                  setFieldValue('reasonCode', newValue?.code);
                }}
                validationMessage={
                  (errors?.reasonCode && touched.reasonCode) ? errors.reasonCode : undefined
                }
                isLoading={isLoadingReasonCodes}
                disabled={isLoadingReasonCodes}
              />
            </div>

            <div className={`${styles['pause-contract-line-main-row']} mt-2`}>
                            <div className={styles['row-field']}>
                              <TopLabel label="Pause Billing Start Date" id="startDate" />
                              <DatePickerTag4
                                id="startDate"
                                onChange={({ value }) => {
                                  setFieldTouched('startDate', true, false);
                                  setFieldValue('startDate', value);
                                }}
                                disabled={isLoadingReasonCodes || isDisableFutureLinePause}
                                value={
                                  values.startDate && isValidDate(new Date(values.startDate)) 
                                    ? new Date(values.startDate) 
                                    : undefined
                                }
                              />
                              {errors?.startDate && touched.startDate && (
                                <ValidationAlert isFloating={false}>{errors.startDate}</ValidationAlert>
                              )}
                            </div>

              <div className={styles['row-field']}>
                <TopLabel required label="Pause Billing End Date" id="endDate" />
                <DatePickerTag4
                  id="endDate"
                  onChange={({ value }) => {
                    setFieldTouched('endDate', true, false);
                    setFieldValue('endDate', value);
                  }}
                  disabled={isLoadingReasonCodes}
                  value={values.endDate && isValidDate(new Date(values.endDate)) ? new Date(values.endDate) : undefined}
                />
                {errors?.endDate && touched.endDate && (
                  <ValidationAlert isFloating={false}>{errors.endDate}</ValidationAlert>
                )}
              </div>

              <ModalFormTextInput
                id="reference"
                labelTitle="Reference"
                className={styles['row-field']}
                placeholder='Enter reference'
                value={values.reference ?? undefined}
                onChange={(event) => {
                  setFieldTouched('reference', true, false);
                  setFieldValue('reference',
                    FormattingUtils.replaceControlCharacters(event.target.value)
                      ? event.target.value
                      : null);
                }}
              />
            </div>
          </div>

          <div className={styles['pause-contract-line-comment']}>
            <ModalFormTextarea
              id="comment"
              labelTitle="Comment"
              className={styles['pause-contract-line-comment-field']}
              placeholder="Enter short comments"
              value={values.comment ?? undefined}
              onChange={(event) => {
                setFieldTouched('comment', true, false);
                setFieldValue('comment',
                  FormattingUtils.replaceControlCharacters(event.target.value)
                    ? event.target.value
                    : null);
              }}
              validationMessage={
                errors?.comment && touched.comment ? errors.comment : undefined
              }
              isFloatingValidation={true}
            />
          </div>
        </div>

        <div className={styles.button}>
          <Button
            className={styles['button-save']}
            disabled={isUpdate ? (!isValid || !dirty || readOnly) : (!isValid || readOnly)}
            buttonStyleType={ButtonStyleType.PRIMARY}
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>

      <div className={styles['grid-container']}>
        <TableGrid<any>
          ref={gridRef}
          domLayout="normal"
          getRowId={(params) => params.data.id || params.data.iD}
          suppressRowClickSelection={true}
          rowData={rowData}
          columnDefs={columns}
          rowSelection="multiple"
          pagination={false}
          suppressPaginationPanel={true}
          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: true,
            suppressSizeToFit: false
          }}
          components={{
            SelectableFilter: AgGridSelectableFilter,
            DatepickerEditor: AgGridDatepickerTag4Editor,
            DropdownEditor: AgGridDropdownEditor,
            CellRenderer: AgGridCellRenderer,
          }}
          enableRangeSelection={true}
          allowContextMenuWithControlKey={true}
          alwaysMultiSort={true}
          headerHeight={40}
          rowHeight={45}
          context={{}}
        />
      </div>

      <div className={styles.button}>
        <Button
          className={styles['button-save']}
          disabled={!areAllEditedRowsValid || readOnly}
          buttonStyleType={ButtonStyleType.PRIMARY}
          onClick={saveAllEditedRows}
        >
          Save
        </Button>
      </div>

      <ActionButtonsWrapper
        leftButton={(
          <Button
            buttonStyleType={ButtonStyleType.SECONDARY}
            onClick={onClose}
          >
            Cancel
          </Button>
        )}
      />
    </AgModalWindow>
  );
}
